import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PROVINCES, SERVICES } from "../../lib/constants";
import { ArrowRight, Loader2 } from "lucide-react";
import { getAttribution, trackConversion, quoteConversionRef } from "../../lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INIT = {
  full_name: "",
  email: "",
  phone: "",
  vehicle_type: "rv",
  vehicle_details: "",
  pickup_city: "",
  pickup_province: "",
  delivery_city: "",
  delivery_province: "",
  preferred_date: "",
  message: "",
};

const inputCls =
  "w-full bg-[#F4F4F1] border-2 border-[#122A1E] px-4 py-3 font-body text-[#0A0E0C] placeholder:text-[#4A5550]/60 focus:outline-none focus:ring-0 focus:border-[#FF5A00] focus:shadow-[4px_4px_0px_0px_#FF5A00] transition-shadow rounded-none";

const labelCls = "font-mono-accent text-[10px] uppercase text-[#4A5550] mb-2 block tracking-[0.2em]";

export default function QuoteForm() {
  const [form, setForm] = useState(INIT);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    // Basic client validation
    const required = ["full_name", "email", "phone", "pickup_city", "pickup_province", "delivery_city", "delivery_province"];
    for (const k of required) {
      if (!form[k]?.trim()) {
        toast.error("Please fill all required fields.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const attribution = getAttribution() || {};
      await axios.post(`${API}/quotes`, { ...form, ...attribution });
      trackConversion(quoteConversionRef());
      toast.success("Quote request received. Dispatch will contact you within one business day.");
      setSubmitted(true);
      setForm(INIT);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again or call dispatch.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="quote" className="bg-[#F4F4F1] border-b-2 border-[#122A1E]" data-testid="quote-section-success">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <div className="font-mono-accent text-[11px] uppercase text-[#FF5A00] mb-4">◆ Request Received</div>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl text-[#0A0E0C] leading-none">
            Thanks — we've got it.
          </h2>
          <p className="mt-6 text-[#4A5550] text-lg max-w-xl mx-auto">
            A real human from dispatch will reach out within one business day with a
            firm, no-broker price and availability.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="mt-8 inline-flex items-center gap-2 bg-[#122A1E] hover:bg-[#0C1D14] text-white font-display font-bold uppercase text-sm tracking-wider px-6 py-4 border-2 border-[#122A1E]"
            data-testid="quote-submit-another"
          >
            Submit Another Quote <ArrowRight size={16} />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="quote" className="bg-[#F4F4F1] border-b-2 border-[#122A1E]" data-testid="quote-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="font-mono-accent text-[11px] uppercase text-[#4A5550] mb-3">◆ Instant Quote</div>
            <h2 className="font-display font-black uppercase tracking-tighter leading-none text-[#0A0E0C] text-4xl md:text-5xl">
              Get a Direct
              <br />
              <span className="text-[#FF5A00]">Carrier Quote.</span>
            </h2>
            <p className="mt-5 text-[#4A5550] leading-relaxed">
              Tell us what's moving, where it's moving to, and when. We'll reply
              with a firm price — no broker markup, no surprises.
            </p>
            <ul className="mt-6 space-y-2 font-mono-accent text-[10px] uppercase text-[#4A5550]">
              <li>◆ No credit card required</li>
              <li>◆ Response within 1 business day</li>
              <li>◆ Quotes valid for 14 days</li>
            </ul>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-8 border-2 border-[#122A1E] bg-[#EAEAE5] p-6 md:p-10 hard-shadow"
            data-testid="quote-form"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input
                  type="text"
                  required
                  value={form.full_name}
                  onChange={update("full_name")}
                  className={inputCls}
                  placeholder="John Tremblay"
                  data-testid="quote-input-name"
                />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  className={inputCls}
                  placeholder="you@email.ca"
                  data-testid="quote-input-email"
                />
              </div>
              <div>
                <label className={labelCls}>Phone *</label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={update("phone")}
                  className={inputCls}
                  placeholder="(555) 123-4567"
                  data-testid="quote-input-phone"
                />
              </div>
              <div>
                <label className={labelCls}>Vehicle Type *</label>
                <select
                  value={form.vehicle_type}
                  onChange={update("vehicle_type")}
                  className={inputCls}
                  data-testid="quote-select-vehicle"
                >
                  {SERVICES.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.title}
                    </option>
                  ))}
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="md:col-span-2 pt-2 pb-1">
                <div className="font-mono-accent text-[11px] uppercase text-[#FF5A00] border-b-2 border-[#122A1E] pb-2">
                  ◆ Pickup Location
                </div>
              </div>
              <div>
                <label className={labelCls}>Pickup City *</label>
                <input
                  type="text"
                  required
                  value={form.pickup_city}
                  onChange={update("pickup_city")}
                  className={inputCls}
                  placeholder="Calgary"
                  data-testid="quote-input-pickup-city"
                />
              </div>
              <div>
                <label className={labelCls}>Pickup Province *</label>
                <select
                  required
                  value={form.pickup_province}
                  onChange={update("pickup_province")}
                  className={inputCls}
                  data-testid="quote-select-pickup-province"
                >
                  <option value="">Select</option>
                  {PROVINCES.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2 pt-2 pb-1">
                <div className="font-mono-accent text-[11px] uppercase text-[#FF5A00] border-b-2 border-[#122A1E] pb-2">
                  ◆ Delivery Location
                </div>
              </div>
              <div>
                <label className={labelCls}>Delivery City *</label>
                <input
                  type="text"
                  required
                  value={form.delivery_city}
                  onChange={update("delivery_city")}
                  className={inputCls}
                  placeholder="Halifax"
                  data-testid="quote-input-delivery-city"
                />
              </div>
              <div>
                <label className={labelCls}>Delivery Province *</label>
                <select
                  required
                  value={form.delivery_province}
                  onChange={update("delivery_province")}
                  className={inputCls}
                  data-testid="quote-select-delivery-province"
                >
                  <option value="">Select</option>
                  {PROVINCES.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Preferred Date</label>
                <input
                  type="date"
                  value={form.preferred_date}
                  onChange={update("preferred_date")}
                  className={inputCls}
                  data-testid="quote-input-date"
                />
              </div>
              <div>
                <label className={labelCls}>Vehicle Details</label>
                <input
                  type="text"
                  value={form.vehicle_details}
                  onChange={update("vehicle_details")}
                  className={inputCls}
                  placeholder="Year · make · model · dimensions"
                  data-testid="quote-input-vehicle-details"
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Additional Notes</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  className={`${inputCls} resize-none`}
                  placeholder="Access notes, timing flexibility, anything special about the unit…"
                  data-testid="quote-input-notes"
                />
              </div>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <div className="font-mono-accent text-[10px] uppercase text-[#4A5550]">
                ◆ Your data stays with us. Never sold, never load-boarded.
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] disabled:opacity-60 disabled:cursor-not-allowed text-white font-display font-bold uppercase text-sm tracking-wider px-7 py-4 border-2 border-[#122A1E] hard-shadow-sm hover-lift"
                data-testid="quote-form-submit"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                {submitting ? "Submitting…" : "Request Quote"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
